import type { WorkBook } from 'xlsx';

import { hasAllHeaders, headerFingerprint } from './cells';
import type { DailyReportKind } from './types';
import { sheetToAoa } from './workbookSheets';

/**
 * Header sets unique enough to name a report from its first few rows.
 * More-specific fingerprints come first so STOCKS (product + stock access)
 * wins over STOCK (stock access only).
 */
const HEADER_FINGERPRINTS: ReadonlyArray<{
  kind: Exclude<DailyReportKind, 'unknown'>;
  required: readonly string[];
}> = [
  {
    kind: 'suspendedItem',
    required: ['ITEM#', 'WRH LOCATION', 'STK Y/N'],
  },
  {
    // Saved with AS/400 field names instead of headings.
    kind: 'suspendedItem',
    required: ['ITEMNOX', 'LOCATION', 'STKYN'],
  },
  {
    kind: 'supersede',
    required: ['OLD ITEM NUMBER', 'NEW ITEM NUMBER'],
  },
  {
    kind: 'vendorsCreated',
    required: ['SUPPLIER NO.', 'ACCT. CREATED DATE'],
  },
  {
    kind: 'potfUpc',
    required: ['UPC', 'CODE'],
  },
  {
    kind: 'webNoPrice',
    required: ['USER DEF-2', 'USER DEF-9'],
  },
  {
    kind: 'supersededWithStock',
    required: ['PRODUCT ACCESS COMPANY', 'STOCK ACCESS COMPANY'],
  },
  {
    kind: 'rdStockWithoutItem',
    required: ['STOCK ACCESS COMPANY', 'STOCK RECORD ID'],
  },
  {
    kind: 'vendorItemList',
    required: ['SUNAME', 'CATEGORY3'],
  },
];

/** Rows a sheet must show for {@link detectDailyReportFromHeads}. */
export const DETECT_HEAD_ROWS = 3;

function detectSheetKind(rows: unknown[][]): DailyReportKind | null {
  const names = headerFingerprint(rows, DETECT_HEAD_ROWS);
  for (const rule of HEADER_FINGERPRINTS) {
    if (hasAllHeaders(names, rule.required)) return rule.kind;
  }
  return null;
}

function detectFromHeaders(
  sheets: Iterable<unknown[][]>,
): DailyReportKind | null {
  for (const rows of sheets) {
    const kind = detectSheetKind(rows);
    if (kind) return kind;
  }
  return null;
}

function* workbookSheetRows(workbook: WorkBook): Generator<unknown[][]> {
  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (sheet) yield sheetToAoa(sheet);
  }
}

/** Stem of `ITEMSUSPE4.XLS` → `ITEMSUSPE4`, uppercased. */
export function fileStem(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '').toUpperCase();
}

/**
 * Filename markers in precedence order. `STOCKS` must sit above `STOCK_` so
 * STOCKS_V is not classified as the RD stock-without-item report.
 */
const FILENAME_MARKERS: ReadonlyArray<{
  needle: string;
  kind: Exclude<DailyReportKind, 'unknown'>;
}> = [
  { needle: 'ITEMSUSPE', kind: 'suspendedItem' },
  { needle: 'SUPERCE', kind: 'supersede' },
  { needle: 'SUPERSED', kind: 'supersede' },
  { needle: 'POTF_UPC', kind: 'potfUpc' },
  { needle: 'VDRCRTDATE', kind: 'vendorsCreated' },
  { needle: 'WEBNOPRF', kind: 'webNoPrice' },
  { needle: 'STOCKS', kind: 'supersededWithStock' },
  { needle: 'STOCK_', kind: 'rdStockWithoutItem' },
  { needle: 'VITMLIST', kind: 'vendorItemList' },
];

function detectFromFilename(fileName: string): DailyReportKind {
  const stem = fileStem(fileName);
  const match = FILENAME_MARKERS.find((rule) => stem.includes(rule.needle));
  return match?.kind ?? 'unknown';
}

/**
 * Name the daily report. Headers win so a renamed file still classifies;
 * the AS/400 export filename is the fallback when the sheet is headers-only
 * or the first rows are a banner.
 */
export function detectDailyReport(
  workbook: WorkBook,
  fileName: string,
): DailyReportKind {
  return (
    detectFromHeaders(workbookSheetRows(workbook)) ??
    detectFromFilename(fileName)
  );
}

/**
 * {@link detectDailyReport} from the first {@link DETECT_HEAD_ROWS} rows of
 * each sheet, for a workbook read as a stream.
 */
export function detectDailyReportFromHeads(
  heads: unknown[][][],
  fileName: string,
): DailyReportKind {
  return detectFromHeaders(heads) ?? detectFromFilename(fileName);
}
